import {useConfig} from '@aboutyou/config';
import {useCurrencyConfig, useTranslation} from '@aboutyou/i18n';
import {FadeTransition} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {FC, Fragment, useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Price, PriceV2} from '../../../types/Price';
import {Flyout} from '../../atoms/Flyout/Flyout';
import {ModalDialog} from '../../atoms/ModalDialog/ModalDialog';
import {Text} from '../../atoms/Text/Text';
import {FinalPrice, FinalPriceVatWrapper} from '../FinalPrice/FinalPrice';
import {LowestProductPrice30DMoreInfo} from '../LowestProductPrice30D/LowestProductPrice30DMoreInfo';
import {LowestProductPrice30DWithRichText} from '../LowestProductPrice30D/LowestProductPrice30DWithRichText';
import {OriginalPrice} from '../OriginalPrice/OriginalPrice';
import {ReductionsAndPriceTags} from './Components/ReductionsAndPriceTags';
import {
  Badge,
  PriceAndReductionsInline,
  SalesCell,
  SalesRow,
} from './Components/Shared';

interface PriceBoxSharedProps {
  isSizeSelected?: boolean;
  showLargerFont?: boolean;
  showSmallerFont?: boolean;
  showSecondaryPriceLabel?: boolean;
  showVatInNewLine?: boolean;
  hideReductions?: boolean;
  hideBadges?: boolean;
  hideLpl30?: boolean;
  hideVat?: boolean;
  hideFromLabel?: boolean;
  showReductionsInline?: boolean;
  showReductionsLargeFont?: boolean;
  overflowHiddenLPL30D?: boolean;
  align?: 'left' | 'right';
  CountdownComponent?: React.ComponentType<{
    endDate: Date;
    backgroundColor?: string;
  }>;
  className?: string;
  reverseReductions?: boolean;
}
export type PriceBoxProps = PriceBoxSharedProps & {
  price: Price | PriceV2;
  productId?: number | string;
};

/**
 * @deprecated
 */
const PriceBoxDeprecated: React.FC<PriceBoxProps & {price: Price}> = React.memo(
  ({
    price,
    isSizeSelected,
    showLargerFont,
    showSecondaryPriceLabel,
    showVatInNewLine,
    hideReductions,
    hideBadges,
    hideLpl30,
    hideVat,
    hideFromLabel,
    showReductionsInline = false,
    showReductionsLargeFont = false,
    showSmallerFont = false,
    align = 'left',
    CountdownComponent,
    reverseReductions,
    className,
  }) => {
    const {t} = useTranslation();
    const {hideVatInfo} = useCurrencyConfig();

    const priceIncludesTaxes = !hideVatInfo;

    const reductionsNode = (
      <ReductionsAndPriceTags
        price={price}
        hideReductions={hideReductions}
        CountdownComponent={CountdownComponent}
        align={align}
        hideBadges={hideBadges}
        reverseReductions={reverseReductions}
      />
    );

    const finalPriceNode = useMemo(
      () => (
        <FinalPriceRow
          data-has-secondary-price-label={Boolean(
            showSecondaryPriceLabel && price.secondaryPriceLabel,
          )}
          data-show-vat-inline={showVatInNewLine}
          $align={align}
        >
          <FinalPriceWrapper data-testid="finalPrice">
            <StyledFinalPrice
              price={price}
              isSizeSelected={isSizeSelected}
              hideFromLabel={hideFromLabel}
            />
          </FinalPriceWrapper>
          {!hideVat && priceIncludesTaxes && (
            <TaxText data-testid="taxText">{t('including VAT')}</TaxText>
          )}
        </FinalPriceRow>
      ),
      [
        align,
        hideVat,
        isSizeSelected,
        price,
        priceIncludesTaxes,
        showSecondaryPriceLabel,
        showVatInNewLine,
        t,
      ],
    );

    return (
      <FadeTransition duration={500} className={className}>
        <Wrapper
          data-testid="priceBox"
          data-largerfont={showLargerFont}
          data-smallerfont={showSmallerFont}
          data-reductions-large-font={showReductionsLargeFont}
        >
          {showReductionsInline ? (
            <PriceAndReductionsInline>
              {finalPriceNode}
              {reductionsNode}
            </PriceAndReductionsInline>
          ) : (
            <>
              {reductionsNode}
              {finalPriceNode}
            </>
          )}
          {!hideLpl30 && price.lpl30 && (
            <LowestPriorPriceHint data-testid="lowestProductPrice30D">
              {price.lpl30}
            </LowestPriorPriceHint>
          )}
        </Wrapper>
      </FadeTransition>
    );
  },
);
PriceBoxDeprecated.displayName = 'PriceBox';

interface FinalPriceComponentProps {
  showSecondaryPriceLabel?: boolean;
  price: PriceV2;
  showVatInNewLine?: boolean;
  align: 'left' | 'right';
  isSizeSelected?: boolean;
  hideFromLabel?: boolean;
  hideVat?: boolean;
  lowestPrice30DProductStateManagerKey?: string;
}

const FinalPriceComponent: FC<FinalPriceComponentProps> = ({
  showSecondaryPriceLabel,
  price,
  showVatInNewLine,
  align,
  isSizeSelected,
  hideFromLabel,
  hideVat,
  lowestPrice30DProductStateManagerKey,
}) => {
  const {device} = useConfig();
  const showLocalVatLabel = Boolean(
    !showSecondaryPriceLabel && showVatInNewLine,
  );
  return (
    <FinalPriceRow
      data-has-secondary-price-label={Boolean(
        showSecondaryPriceLabel && price.finalPrice.secondaryPriceLabel,
      )}
      data-show-vat-inline={showVatInNewLine}
      $removeGap={showLocalVatLabel && device === 'mobile'}
      $align={align}
    >
      <FinalPriceWrapper data-testid="finalPrice">
        <StyledFinalPrice
          price={price}
          isSizeSelected={isSizeSelected}
          hideFromLabel={hideFromLabel}
          hideVatLabel={showLocalVatLabel || hideVat}
          align={align}
          lowestPrice30DProductStateManagerKey={
            lowestPrice30DProductStateManagerKey
          }
        />
      </FinalPriceWrapper>
      {price.finalPrice.vatLabel && showLocalVatLabel && !hideVat && (
        <FinalPriceVatWrapper
          dangerouslySetInnerHTML={{__html: price.finalPrice.vatLabel.text}}
        />
      )}
    </FinalPriceRow>
  );
};

const PriceBoxNew: React.FC<PriceBoxProps & {price: PriceV2}> = React.memo(
  ({
    price,
    isSizeSelected,
    showLargerFont,
    showSecondaryPriceLabel,
    showVatInNewLine,
    hideBadges,
    hideLpl30,
    hideFromLabel,
    hideVat,
    showReductionsLargeFont = false,
    showSmallerFont = false,
    align = 'left',
    CountdownComponent,
    className,
    productId,
    overflowHiddenLPL30D,
  }) => {
    const hasCountdown = Boolean(
      price.badge.some(reduction => CountdownComponent && reduction.endsAt),
    );

    const lowestPrice30DProductStateManagerKey = `pricebox-${productId}`;

    return (
      <FadeTransition duration={500} className={className}>
        <Wrapper
          data-testid="priceBox"
          data-largerfont={showLargerFont}
          data-smallerfont={showSmallerFont}
          data-reductions-large-font={showReductionsLargeFont}
        >
          <SalesRow data-has-countdown={hasCountdown} $align={align}>
            {!hideBadges && (
              <SalesCell>
                {price.badge.map((reduction, index) => {
                  if (!reduction) {
                    return null;
                  }

                  return (
                    <Fragment key={reduction.text + index}>
                      {CountdownComponent && reduction.endsAt && (
                        <CountdownComponent
                          endDate={reduction.endsAt}
                          backgroundColor={reduction.backgroundColor.hex}
                        />
                      )}
                      <Badge
                        data-testid="badge-sale"
                        data-has-countdown={Boolean(
                          CountdownComponent && reduction.endsAt,
                        )}
                        $foregroundColor={reduction.foregroundColor.hex}
                        $backgroundColor={reduction.backgroundColor.hex}
                        data-untranslated-label="sale"
                      >
                        {reduction.text}
                      </Badge>
                    </Fragment>
                  );
                })}
              </SalesCell>
            )}
          </SalesRow>
          <FinalPriceComponent
            price={price}
            isSizeSelected={isSizeSelected}
            showSecondaryPriceLabel={showSecondaryPriceLabel}
            showVatInNewLine={showVatInNewLine}
            align={align}
            hideFromLabel={hideFromLabel}
            hideVat={hideVat}
            lowestPrice30DProductStateManagerKey={
              lowestPrice30DProductStateManagerKey
            }
          />
          <OriginalPrice price={price} />
          {!hideLpl30 && price.lpl30d && (
            <LowestProductPrice30DWithRichText
              lpl30d={price.lpl30d}
              align={align}
              overflowHiddenLPL30D={overflowHiddenLPL30D}
            />
          )}
          {price.each && (
            <>
              <EachPrice
                data-testid="eachPrice"
                dangerouslySetInnerHTML={{__html: price.each.text}}
                data-align={align}
              />
            </>
          )}
        </Wrapper>
      </FadeTransition>
    );
  },
);
PriceBoxNew.displayName = 'PriceBox';

export const PriceBox: FC<
  PriceBoxProps & {price: Price | PriceV2; productId?: number | string}
> = props => {
  const {device} = useConfig();

  return 'finalPrice' in props.price ? (
    <>
      <PriceBoxNew {...props} price={props.price} />
      <LowestProductPrice30DMoreInfo
        showCloseButton={device === 'mobile'}
        LayoutComponent={device === 'mobile' ? Flyout : ModalDialog}
        lpl30d={props.price.lpl30d}
        lowestPrice30DProductStateManagerKey={`pricebox-${props.productId}`}
      />
    </>
  ) : (
    <PriceBoxDeprecated {...props} price={props.price as Price} />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const FinalPriceRow = styled.div<{
  $align: 'left' | 'right';
  $removeGap?: boolean;
}>`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${({$removeGap}) => ($removeGap ? '0' : '4px')};
  justify-content: ${({$align}) =>
    $align === 'right' ? 'flex-end' : 'flex-start'};

  &[data-show-vat-inline='true'] {
    flex-direction: column;
    align-items: ${props =>
      props.$align === 'right' ? 'flex-end' : 'baseline'};
  }

  &[data-has-secondary-price-label='true'] {
    flex-direction: column;
    gap: 0;
  }
`;

const FinalPriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const StyledFinalPrice = styled(FinalPrice)`
  font-size: ${FONT_SIZES.px16};

  [data-smallerfont='true'] & {
    font-size: ${FONT_SIZES.px12};
  }

  [data-largerfont='true'] & {
    font-size: ${FONT_SIZES.px20};
  }
`;

const TaxText = styled(Text)`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.6;
  color: ${COLORS['Text.Grey']};
`;

const LowestPriorPriceHint = styled(Text)`
  font-size: ${FONT_SIZES.px9};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.55;
  color: ${COLORS['Text.Grey']};
`;

const EachPrice = styled.div`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;
  color: ${COLORS['Text.Grey']};

  &[data-align='right'] {
    text-align: right;
  }
`;
